import React, {
  useState,
  useRef,
  useCallback,
  useEffect,
  PropsWithChildren
} from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { AuthContext } from "../contexts";
import {
  refreshToken,
  verifyCode,
  verifyToken,
  getRefreshToken,
  removeTokens,
  storeTokens,
  hasTokenExpired
} from "../services";
import { LoaderFullscreen } from "modules/core/components";
import { LOGIN_URL, LOGOUT_URL, SIGN_UP_URL } from "../constants";
import { User } from "modules/user/types";
import { Token } from "../types";

const TEN_MINS = 60 * 10 * 1000;

export const AuthProvider = ({ children }: PropsWithChildren) => {
  const [user, setUser] = useState<User>();
  const [tokens, setTokens] = useState<Token>();
  const [searchParams, setSearchParams] = useSearchParams();
  const { pathname } = useLocation();

  const [isAuthComplete, setIsAuthComplete] = useState(false);

  const refreshTokenInterval = useRef<NodeJS.Timer>();
  const localRefreshToken = getRefreshToken();

  const redirectCodeRequiresExecution = useRef(true);
  const refreshTokenCodeRequiresExecution = useRef(true);

  const navigate = useNavigate();

  const handleAuthError = (error?: any) => {
    setUser(undefined);
    setTokens(undefined);
    removeTokens();
    setSearchParams({});
    setIsAuthComplete(true);
    if (error) console.error(error);
  };

  // Compute isAuthComplete
  useEffect(() => {
    const code = searchParams.get("code");

    if (!code && !localRefreshToken) setIsAuthComplete(true);
  }, []);

  // With Redirect Code
  useEffect(() => {
    const code = searchParams.get("code");

    if (!code) return;
    if (localRefreshToken && !hasTokenExpired()) return;

    setSearchParams({});

    if (!redirectCodeRequiresExecution.current) return;
    redirectCodeRequiresExecution.current = false;

    verifyCode(code).then(setTokens).catch(handleAuthError);
  }, []);

  // With Local Refresh Token
  useEffect(() => {
    if (!localRefreshToken || hasTokenExpired()) return;

    if (!refreshTokenCodeRequiresExecution.current) return;
    refreshTokenCodeRequiresExecution.current = false;

    refreshToken(localRefreshToken).then(setTokens).catch(handleAuthError);
  }, []);

  // refreshToken interval
  useEffect(() => {
    const currentPath = pathname === "/verify-code" ? "/home" : pathname;
    verifyToken(tokens?.accessToken ?? "")
      .then(({ user }) => {
        setUser(user);
        navigate(currentPath);
      })
      .catch(handleAuthError)
      .finally(() => setIsAuthComplete(true));

    refreshTokenInterval.current = setInterval(() => {
      const currentRefreshToken = getRefreshToken();
      if (!currentRefreshToken) return;

      refreshToken(currentRefreshToken).then(setTokens).catch(handleAuthError);
    }, TEN_MINS);
    return () => clearInterval(refreshTokenInterval.current);
  }, [tokens]);

  // Stores tokens in local storage when token state changes
  useEffect(() => tokens && storeTokens(tokens), [tokens]);

  const handleLogout = useCallback(() => {
    handleAuthError();
    setIsAuthComplete(false);

    setTimeout(() => window.location.assign(LOGOUT_URL), 250);
  }, []);

  const handleSignUp = useCallback(
    () => window.location.assign(SIGN_UP_URL),
    []
  );

  const handleLogin = useCallback(() => {
    //if (user) return;

    window.location.assign(LOGIN_URL);
  }, [user]);

  if (!isAuthComplete) return <LoaderFullscreen />;

  return (
    <AuthContext.Provider
      value={{
        user,
        tokens,
        isAuthComplete,
        login: handleLogin,
        logout: handleLogout,
        signUp: handleSignUp
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
